import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { AvatarChip } from "./AvatarChip";

/**
 * MobileNavDrawer — full-height slide-in for phones / tablets.
 *
 * Behaviour
 * ---------
 * * The trigger (burger button) is rendered by the parent Navbar.
 *   This component owns the open/close state via the `open`/`onClose`
 *   props but renders nothing when closed (so it stays out of the
 *   accessibility tree).
 * * Auto-closes on route change (the user just clicked a nav item).
 * * Body scroll-lock while open so the drawer doesn't get scrolled
 *   off-screen on iOS Safari's bounce.
 *
 * What it shows
 * -------------
 * * Every navigation entry from the desktop nav — DASHBOARD, PLAYERS,
 *   LEAGUES, **TEAMS**, RANKINGS, LFG, optional WALLET, PRICING,
 *   CONTACT. (Previously hidden on phones — see the regression report.)
 * * The user card + INBOX + LOGOUT when signed in.
 * * SIGN IN buttons (Discord / Google) when signed out — also
 *   previously invisible on phones because the desktop renderer
 *   gates them behind `hidden md:flex`.
 */
export function MobileNavDrawer({ open, onClose, navItems, onSignIn, onOpenProfile }) {
  const { user, logout, inboxCount } = useAuth();
  const { flags } = useFeatureFlags();
  const location = useLocation();
  const hasMail = (inboxCount || 0) > 0;

  // Close automatically on route change so navigating doesn't leave
  // the drawer open with stale state behind it.
  useEffect(() => { if (open) onClose?.(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [location.pathname]);

  // Body-scroll lock — without this, iOS Safari lets the page scroll
  // *behind* the drawer when the user drags inside the panel.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const visibleItems = navItems
    .filter((n) => !n.authOnly || user)
    .filter((n) => !n.flag || flags?.[n.flag]);

  return (
    <div
      data-testid="mobile-nav-drawer"
      className="fixed inset-0 z-[60] lg:hidden flex"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="relative ml-auto h-screen max-h-[100dvh] w-[86%] max-w-sm bg-[#070c0b] border-l border-[#14b8a6]/30 flex flex-col pb-[env(safe-area-inset-bottom)]"
        // Stop background tap from also dismissing when clicking content.
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-4 flex items-center justify-between border-b border-white/5">
          <p className="text-[10px] tracking-[4px] font-bold text-[#14b8a6]">MENU</p>
          <button
            type="button"
            onClick={onClose}
            data-testid="mobile-nav-close"
            className="text-white/60 hover:text-white text-xl w-9 h-9 inline-flex items-center justify-center"
            aria-label="Close menu"
          >
            ×
          </button>
        </header>

        {user && (
          <button
            type="button"
            onClick={() => onOpenProfile?.()}
            data-testid="mobile-nav-profile-card"
            className="m-4 flex items-center gap-3 p-3 rounded-2xl bg-black/40 border border-white/10 hover:border-[#14b8a6]/40 text-left"
          >
            <AvatarChip avatarId={user.avatar_id} pfpUrl={user.pfp_url} size={42} />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white truncate">{user.name}</p>
              <p className="text-[10px] text-[#14b8a6] tracking-widest">
                {(user.user_type || "casual").toUpperCase()} • {(user.plan || "free").toUpperCase()}
              </p>
            </div>
            <i className="fas fa-chevron-right text-white/30 text-xs" />
          </button>
        )}

        <nav className="flex-1 overflow-y-auto px-2 pb-4">
          <ul className="space-y-1">
            {visibleItems.map((n) => {
              const isActive = location.pathname === n.route;
              return (
                <li key={n.id}>
                  <Link
                    to={n.route}
                    data-testid={`mobile-nav-${n.id}`}
                    onClick={onClose}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] tracking-[2px] font-semibold uppercase ${
                      isActive
                        ? "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
                        : "text-white/75 hover:bg-white/5"
                    }`}
                  >
                    <i className={`fas ${iconFor(n.id)} text-[#14b8a6] w-5 text-center`} />
                    <span>{n.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>

          {user && (
            <>
              <div className="mt-4 mx-2 h-px bg-white/5" />
              <button
                type="button"
                onClick={() => { onOpenProfile?.("inbox"); onClose?.(); }}
                data-testid="mobile-nav-inbox"
                className="mt-3 w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-[13px] tracking-[2px] font-semibold uppercase text-white/75 hover:bg-white/5"
              >
                <span className="flex items-center gap-3">
                  <i className={`fas ${hasMail ? "fa-envelope" : "fa-inbox"} text-[#14b8a6] w-5 text-center`} />
                  INBOX
                </span>
                {hasMail && (
                  <span className="min-w-[22px] h-5 px-1.5 rounded-full bg-[#14b8a6]/20 border border-[#14b8a6]/55 text-[#14b8a6] text-[10px] font-bold flex items-center justify-center">
                    {inboxCount > 99 ? "99+" : inboxCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => { logout(); onClose?.(); }}
                data-testid="mobile-nav-logout"
                className="mt-1 w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] tracking-[2px] font-semibold uppercase text-white/55 hover:bg-white/5"
              >
                <i className="fas fa-right-from-bracket text-white/40 w-5 text-center" />
                LOG OUT
              </button>
            </>
          )}
        </nav>

        {!user && (
          <footer className="p-4 border-t border-white/5 space-y-2">
            <button
              type="button"
              onClick={() => { onSignIn?.(); onClose?.(); }}
              data-testid="mobile-nav-discord"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border border-[#5865F2]/70 text-white font-bold"
            >
              <i className="fab fa-discord" /> CONTINUE WITH DISCORD
            </button>
            <button
              type="button"
              onClick={() => { onSignIn?.(); onClose?.(); }}
              data-testid="mobile-nav-google"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border border-white/30 text-white font-bold"
            >
              <i className="fab fa-google text-[#DB4437]" /> CONTINUE WITH GOOGLE
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
}

function iconFor(id) {
  switch (id) {
    case "dashboard":    return "fa-house";
    case "players":      return "fa-users";
    case "leagues":      return "fa-trophy";
    case "hqs":          return "fa-shield-halved";
    case "leaderboards": return "fa-chart-line";
    case "lfg":          return "fa-magnifying-glass";
    case "wallet":       return "fa-wallet";
    case "pricing":      return "fa-bolt";
    case "contact":      return "fa-envelope-open-text";
    default:             return "fa-circle";
  }
}

/** Burger trigger — render in Navbar where the desktop nav is hidden. */
export function MobileNavBurger({ onClick, hasMail }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="mobile-nav-burger"
      aria-label="Open menu"
      className="relative inline-flex lg:hidden items-center justify-center w-11 h-11 rounded-xl border border-white/10 bg-black/40 text-white hover:border-[#14b8a6]/50"
    >
      <i className="fas fa-bars" />
      {hasMail && (
        <span
          aria-hidden
          className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#14b8a6] border border-black"
        />
      )}
    </button>
  );
}
